import { useState } from "react";

import {
  auditStamp,
  MCP_CLIENTS_PATH,
  MCP_CLIENTS_REQUESTED_PATH,
  mcpClientsOf,
  redirectHost,
  requestedAppsOf,
  type McpClients,
  type McpRequestedApps,
} from "../../api/mcp";
import { AppAccessControl } from "../../components/mcp/AppAccessControl";
import { CouldNotLook, Denied, LoadFailed, Loading, SignedOut } from "../../components/states";
import { Badge, Card, Empty, Notice, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { Input } from "../../f7";
import type { Resource } from "../../hooks/useResource";
import { useResource } from "../../hooks/useResource";

/**
 * Which AI apps may connect to MCP.
 *
 * An app is allowed by its REDIRECT URI: the address MCP sends a merchant's
 * sign-in result to. Allowing one is a decision to hand merchants' tokens to
 * whoever controls that address, so this page is about addresses, not names —
 * a name is whatever the app says it is.
 *
 * Three parts, kept apart on purpose:
 *  - the baseline (Claude, ChatGPT, local desktop apps) is fixed in MCP's
 *    config and read-only here;
 *  - "added by admins" is the only list this page writes, live on every MCP
 *    server within ~60s, no restart;
 *  - "requested" is what MCP refused because its address was not allowed. It is
 *    the demand signal: nothing in it is allowed until someone allows it, and an
 *    app leaves it the moment it is.
 *
 * Removing an app blocks NEW sign-ins only; merchants already connected through
 * it keep working (MCP's decision, and the dialog says so).
 */
/**
 * MCP's sign-in allowlist, as a section.
 *
 * NOT A PAGE ANY MORE. This was a Policy tab at `/policy/apps`; it is now a
 * collapsible section inside MCP's block on Policy -> Settings, because
 * "which apps may connect" is a setting, and Policy's tab strip is for what a
 * change APPLIES TO rather than one tab per knob. `/policy/apps` redirects
 * there, so a link already pasted into a thread still lands somewhere right.
 *
 * It takes no `embedded` flag: the surrounding Card carries the title, so
 * there is no heading here to suppress.
 */
export function AiAppsPanel({ config }: { config: AppConfig }) {
  const clients = useResource<McpClients>(config, MCP_CLIENTS_PATH);
  const requested = useResource<McpRequestedApps>(config, MCP_CLIENTS_REQUESTED_PATH);
  const [typed, setTyped] = useState("");

  // Both lists change on every write: an allowed app leaves "requested".
  function reloadAll() {
    clients.reload();
    requested.reload();
  }

  const typedUri = typed.trim();

  return (
    <>
      <States state={clients.state} what="the allowed apps" onRetry={clients.reload} />
      {clients.state.kind === "ready" &&
        (() => {
          const c = mcpClientsOf(clients.state.data);
          const full = c.extra.length >= c.maxExtra;
          return (
            <>
              <Card title="Always allowed" readOnly note="Fixed in MCP's config; not editable here.">
                <ul className="space-y-1">
                  {c.baseline.map((uri) => (
                    <li key={uri} className="break-all">
                      <span className="ref">{uri}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card
                title="Added by admins"
                aside={
                  <span className="font-mono text-xs tabular-nums text-slate-500 dark:text-slate-400">
                    {c.extra.length} of {c.maxExtra}
                  </span>
                }
              >
                {c.extra.length === 0 ? (
                  <Empty>No apps added. Only the apps above can connect.</Empty>
                ) : (
                  <Table
                    head={
                      <>
                        <Th>App</Th>
                        <Th>Redirect URI</Th>
                        <Th right>{""}</Th>
                      </>
                    }
                  >
                    {c.extra.map((uri) => (
                      <tr key={uri}>
                        <Td>{redirectHost(uri)}</Td>
                        <Td>
                          <span className="ref break-all">{uri}</span>
                        </Td>
                        <Td right>
                          <AppAccessControl config={config} uri={uri} mode="remove" onDone={reloadAll} />
                        </Td>
                      </tr>
                    ))}
                  </Table>
                )}

                <div className="mt-4 space-y-2">
                  <p className="text-sm">
                    Add an app by its redirect URI (https, one exact host, with the callback path).
                  </p>
                  {full ? (
                    <Notice tone="amber">
                      The list is full ({c.maxExtra} apps). Remove one before adding another.
                    </Notice>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="w-96 max-w-full">
                        <Input
                          value={typed}
                          onChange={(ev) => setTyped(ev.target.value)}
                          placeholder="https://app.example.com/oauth/callback"
                          aria-label="Redirect URI to allow"
                          maxLength={300}
                        />
                      </div>
                      {typedUri && (
                        <AppAccessControl
                          config={config}
                          uri={typedUri}
                          mode="allow"
                          label="Allow…"
                          onDone={() => {
                            setTyped("");
                            reloadAll();
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </>
          );
        })()}

      <Card
        title="Requested apps"
        note="Apps that tried to connect and were refused because their address is not allowed. Nothing here is allowed automatically."
      >
        <States state={requested.state} what="the requested apps" onRetry={requested.reload} />
        {requested.state.kind === "ready" &&
          (() => {
            const apps = requestedAppsOf(requested.state.data);
            if (apps.length === 0) return <Empty>No app has been refused recently.</Empty>;
            return (
              <Table
                head={
                  <>
                    <Th>App</Th>
                    <Th>Redirect URI</Th>
                    <Th right>Tries</Th>
                    <Th>Last tried</Th>
                    <Th right>{""}</Th>
                  </>
                }
              >
                {apps.map((a) => (
                  <tr key={a.redirectUri}>
                    <Td>
                      <div>{a.name ?? redirectHost(a.redirectUri)}</div>
                      {!a.knownHost && <Badge tone="amber">unfamiliar domain</Badge>}
                    </Td>
                    <Td>
                      <span className="ref break-all">{a.redirectUri}</span>
                    </Td>
                    <Td right>{a.tries}</Td>
                    <Td>{a.lastSeen ? auditStamp(a.lastSeen) : "—"}</Td>
                    <Td right>
                      {a.canAllow ? (
                        <AppAccessControl
                          config={config}
                          uri={a.redirectUri}
                          mode="allow"
                          knownHost={a.knownHost}
                          onDone={reloadAll}
                        />
                      ) : (
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          Cannot be allowed: {a.problem ?? "not a safe address"}
                        </span>
                      )}
                    </Td>
                  </tr>
                ))}
              </Table>
            );
          })()}
      </Card>
    </>
  );
}

function States<T>({
  state,
  what,
  onRetry,
}: {
  state: Resource<T>;
  what: string;
  onRetry: () => void;
}) {
  return (
    <>
      {state.kind === "loading" && <Loading what={what} />}
      {state.kind === "signedout" && <SignedOut />}
      {state.kind === "denied" && <Denied message={state.error.message} />}
      {state.kind === "unavailable" && (
        <CouldNotLook what={what} message={state.error.message} onRetry={onRetry} />
      )}
      {state.kind === "notfound" && <LoadFailed what={what} message="Route not found." />}
      {state.kind === "invalid" && <LoadFailed what={what} message={state.error.message} />}
      {state.kind === "error" && <LoadFailed what={what} message={state.error.message} />}
    </>
  );
}
